import { useEffect, useSyncExternalStore } from 'react';

/** What the admin picked; `system` follows the OS. */
export type ThemeChoice = 'system' | 'light' | 'dark';
/** What is on screen. */
export type Theme = 'light' | 'dark';

/** The localStorage key. KEEP IN SYNC with `public/theme-init.js`. */
export const THEME_STORAGE_KEY = 'dp-theme';
const LIGHT_QUERY = '(prefers-color-scheme: light)';

function isChoice(value: unknown): value is ThemeChoice {
    return value === 'system' || value === 'light' || value === 'dark';
}

/** The stored choice, or System when there is none or storage is blocked. */
export function readStoredTheme(): ThemeChoice {
    try {
        const value = localStorage.getItem(THEME_STORAGE_KEY);
        return isChoice(value) ? value : 'system';
    } catch {
        return 'system';
    }
}

/** The OS scheme; dark when the browser cannot tell (the app's own look). */
function systemTheme(): Theme {
    return typeof window.matchMedia === 'function' &&
        window.matchMedia(LIGHT_QUERY).matches
        ? 'light'
        : 'dark';
}

export function resolveTheme(choice: ThemeChoice): Theme {
    return choice === 'system' ? systemTheme() : choice;
}

/*
 * One store for the whole app, so the account menu and RootLayout agree. It
 * re-reads storage whenever its first subscriber arrives, and listens to the
 * OS setting and to other tabs while anyone is subscribed.
 */
let choice: ThemeChoice = 'system';
let theme: Theme = 'dark';
const listeners = new Set<() => void>();

function apply() {
    theme = resolveTheme(choice);
    document.documentElement.dataset.theme = theme;
    for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
    if (listeners.size === 0) {
        choice = readStoredTheme();
        apply();
    }
    listeners.add(listener);

    const media =
        typeof window.matchMedia === 'function'
            ? window.matchMedia(LIGHT_QUERY)
            : null;
    const onSystemChange = () => {
        if (choice === 'system') apply();
    };
    const onStorage = (event: StorageEvent) => {
        if (event.key !== THEME_STORAGE_KEY) return;
        choice = readStoredTheme();
        apply();
    };
    media?.addEventListener('change', onSystemChange);
    window.addEventListener('storage', onStorage);

    return () => {
        listeners.delete(listener);
        media?.removeEventListener('change', onSystemChange);
        window.removeEventListener('storage', onStorage);
    };
}

const snapshot = () => `${choice}:${theme}`;

/** Stores the choice (System when storage is blocked) and applies it now. */
export function setThemeChoice(next: ThemeChoice) {
    choice = next;
    try {
        localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
        // Blocked storage: the choice lasts for this page only.
    }
    apply();
}

/**
 * The app's theme (light-mode): the choice (System, Light or Dark, stored in
 * localStorage "dp-theme") and the theme it resolves to, kept on
 * `<html data-theme>`. `public/theme-init.js` sets the attribute before first
 * paint; this keeps it in sync after that, following the OS live while the
 * choice is System and other tabs when they change it. Signed-out pages use
 * it too, so the choice survives signing out on the same browser.
 */
export function useTheme(): {
    choice: ThemeChoice;
    theme: Theme;
    setChoice: (next: ThemeChoice) => void;
} {
    const current = useSyncExternalStore(subscribe, snapshot, snapshot);
    const [currentChoice, currentTheme] = current.split(':') as [
        ThemeChoice,
        Theme,
    ];

    // The first render may come before the store has read storage.
    useEffect(() => {
        document.documentElement.dataset.theme = currentTheme;
    }, [currentTheme]);

    return {
        choice: currentChoice,
        theme: currentTheme,
        setChoice: setThemeChoice,
    };
}
